/*!
 * Oneskin Cross-Sells — vanilla JS for cross-sell / complete-the-look carousels
 * and quick-view / add-to-cart triggers inside `[data-cross-sells]`.
 *
 * Detached from the frontend/ Vite bundle. Loaded via:
 *   <script src="{{ 'cross-sells.js' | asset_url }}" defer="defer"></script>
 *
 * Replaces frontend/scripts/lib/cross-sells.js.
 * Communicates with assets/quick-view-modal.js via native `quick-view:open`
 * CustomEvents (replaces the shared evx bus).
 */
;(() => {
  'use strict'

  const cartRoutes = (window.theme && window.theme.routes && window.theme.routes.cart) || {}
  const cartAddPath = `${cartRoutes.add || '/cart/add'}.js`

  function addItemById(id, quantity) {
    return fetch(cartAddPath, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity }] }),
    })
      .then((r) => r.json())
      .then((res) => {
        if (res.status == '422') {
          throw { code: 422, message: res.description }
        }
        return res
      })
  }

  function initNativeCarousel(node, options = {}) {
    const viewport = node.classList.contains('swiper') ? node : node.querySelector('.swiper')
    if (!viewport) return null

    const track = viewport.querySelector('.swiper-wrapper') || viewport
    track.classList.add('flex', 'snap-x', 'snap-mandatory', 'overflow-x-auto', 'scroll-smooth')
    viewport.classList.add('overflow-hidden')

    const slides = Array.from(track.children)
    slides.forEach((slide) => slide.classList.add('snap-start', 'shrink-0'))

    if (options.slidesPerView) {
      const percent = 100 / options.slidesPerView
      slides.forEach((slide) => {
        slide.style.width = `${percent}%`
      })
    }
    if (options.spaceBetween != null) {
      track.style.gap = `${options.spaceBetween}px`
    }

    const nextButton = node.querySelector('[data-next]')
    const prevButton = node.querySelector('[data-prev]')

    function scrollByAmount(direction) {
      const amount = viewport.clientWidth * 0.8 * direction
      track.scrollBy({ left: amount, behavior: 'smooth' })
    }

    const nextHandler = () => scrollByAmount(1)
    const prevHandler = () => scrollByAmount(-1)
    nextButton?.addEventListener('click', nextHandler)
    prevButton?.addEventListener('click', prevHandler)

    return {
      destroy() {
        nextButton?.removeEventListener('click', nextHandler)
        prevButton?.removeEventListener('click', prevHandler)
      },
    }
  }

  function initCrossSells(node) {
    const crossSellsSliderEl = node.querySelector('[data-cross-sells-slider]')
    const { crossSellsSlider } = crossSellsSliderEl?.dataset || {}
    const carousel = crossSellsSlider
      ? initNativeCarousel(node, { slidesPerView: 1.15, spaceBetween: 8 })
      : null

    const events = []

    node.querySelectorAll('[data-quick-view-trigger]').forEach((trigger) => {
      const handler = (e) => {
        const { productUrl } = e.currentTarget.dataset
        if (!productUrl) return
        document.dispatchEvent(new CustomEvent('quick-view:open', { detail: { productUrl } }))
      }
      trigger.addEventListener('click', handler)
      events.push(() => trigger.removeEventListener('click', handler))
    })

    node.querySelectorAll('[data-add-item-id]').forEach((trigger) => {
      const handler = (e) => {
        const button = e.currentTarget
        const { addItemId } = button.dataset
        if (!addItemId) return

        button.classList.add('loading')
        addItemById(addItemId, 1)
          .then(() => {
            button.classList.remove('loading')
            if (window.NSHybridCart) window.NSHybridCart.open()
          })
          .catch((error) => {
            button.classList.remove('loading')
            console.error('[cross-sells] add to cart failed', error)
          })
      }
      trigger.addEventListener('click', handler)
      events.push(() => trigger.removeEventListener('click', handler))
    })

    return {
      unload() {
        events.forEach((off) => off())
        carousel?.destroy()
      },
    }
  }

  window.OneskinCrossSells = initCrossSells

  const instances = new WeakMap()

  function autoInit(root = document) {
    root.querySelectorAll('[data-cross-sells]').forEach((node) => {
      if (instances.has(node)) return
      instances.set(node, initCrossSells(node))
    })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => autoInit())
  } else {
    autoInit()
  }

  document.addEventListener('cross-sells:reinit', (event) => {
    autoInit(event.detail?.root || document)
  })

  document.addEventListener('shopify:section:load', (event) => {
    autoInit(event.target)
  })
})()
